/** A assinatura vista de dentro da loja: o aviso no topo e o resumo em Identidade.
 *
 * Só o administrador da loja vê — é o combinado entre a loja e a plataforma, e
 * é ele quem resolve. O aviso não bloqueia nada: diz o que está em aberto e
 * desde quando, e some quando o pagamento é registrado.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Api } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { SeloAssinatura } from './AssinaturaDaLoja';
import { Linha, Vazio } from './base';
import { competencia, data, hojeISO, moeda } from '../utils/formato';

/** Dias antes do fim do teste em que o aviso começa a aparecer. */
const AVISAR_FIM_DO_TESTE = 7;

function diasAte(iso: string): number {
  const [a, m, d] = iso.split('-').map(Number);
  const [ha, hm, hd] = hojeISO().split('-').map(Number);
  return Math.round((Date.UTC(a, m - 1, d) - Date.UTC(ha, hm - 1, hd)) / 86_400_000);
}

function useAssinaturaDaLoja() {
  const { pode } = useAuth();
  return useQuery({
    queryKey: ['assinatura-da-loja'],
    queryFn: Api.assinaturaDaLoja,
    enabled: pode('loja:escrever'),
    staleTime: 10 * 60_000,
  });
}

export function AvisoDaAssinatura() {
  const { data: a } = useAssinaturaDaLoja();
  if (!a) return null;

  if (a.situacao === 'em_atraso') {
    return (
      <div className="aviso atencao">
        A mensalidade da plataforma está em aberto desde <strong>{data(a.em_atraso_desde)}</strong> —{' '}
        {a.meses_em_aberto === 1 ? '1 mês' : `${a.meses_em_aberto} meses`}, {moeda(a.valor_em_aberto)}. Detalhes em{' '}
        <Link to="/loja">Identidade</Link>.
      </div>
    );
  }
  if (a.situacao === 'teste' && a.teste_ate && diasAte(a.teste_ate) <= AVISAR_FIM_DO_TESTE) {
    const faltam = diasAte(a.teste_ate);
    return (
      <div className="aviso info">
        O período de teste termina {faltam <= 0 ? 'hoje' : `em ${data(a.teste_ate)}`}. Para seguir usando, combine a
        assinatura — detalhes em <Link to="/loja">Identidade</Link>.
      </div>
    );
  }
  return null;
}

/** O cartão de Identidade: o que a loja paga, até quando está pago, o histórico. */
export function ResumoDaMinhaAssinatura() {
  const { data: a, isLoading } = useAssinaturaDaLoja();
  if (isLoading || !a) return null;

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <SeloAssinatura assinatura={a} />
      </div>
      <div className="grade-2">
        <div>
          <Linha rotulo="Mensalidade">
            {a.cortesia ? 'Cortesia' : a.valor === null ? 'A combinar' : `${moeda(a.valor)} / mês`}
          </Linha>
          <Linha rotulo="Vencimento">{a.dia_vencimento ? `todo dia ${a.dia_vencimento}` : '—'}</Linha>
          {a.teste_ate && <Linha rotulo="Teste">até {data(a.teste_ate)}</Linha>}
        </div>
        <div>
          <Linha rotulo="Pago até">{a.pago_ate ? competencia(a.pago_ate) : '—'}</Linha>
          <Linha rotulo="Próximo vencimento">{a.proximo_vencimento ? data(a.proximo_vencimento) : '—'}</Linha>
          <Linha rotulo="Em aberto" cor={a.meses_em_aberto ? 'var(--rd)' : undefined}>
            {a.meses_em_aberto ? moeda(a.valor_em_aberto) : 'nada'}
          </Linha>
        </div>
      </div>
      <div className="sep" />
      {a.pagamentos.length === 0 ? (
        <Vazio>Nenhum pagamento registrado ainda.</Vazio>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Competência</th>
                <th className="r">Valor</th>
                <th>Pago em</th>
              </tr>
            </thead>
            <tbody>
              {a.pagamentos.map((p) => (
                <tr key={p.id}>
                  <td>{competencia(p.competencia)}</td>
                  <td className="r num">{moeda(p.valor)}</td>
                  <td>{data(p.pago_em)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
